import { NewBookingDeliveryPropType } from 'common/prop-types';
import InputField from 'components/v2/input-field/input-field';
import RadioButton from 'components/v2/radio-button/radio-button';
import SelectField from 'components/v2/select-field/select-field';
import { Col, Row } from 'grab-ui';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  enableItemTypeFreeTextSelector,
  isItemTypeMandatorySelector,
  itemCategoriesSelector,
} from 'selectors/app-selector';
import { bookingConfigurationParcelSizesSelector } from 'selectors/new-booking-selectors';
import { deliveryFormFields } from './common';
import styles from './styles.less';

const ItemDetails = ({ form, delivery, disabled = false }) => {
  const deliveryDetail = _get(delivery, 'detail');
  const maxWeight = _get(delivery, 'config.maxWeight.value', 10);
  const stateParcelSizes = useSelector(bookingConfigurationParcelSizesSelector);
  const parcelTotalSize = _get(
    deliveryDetail,
    deliveryFormFields.parcelTotalSize.fieldName,
  );
  const deliveryConfigParcelSizes = _get(delivery, 'config.parcelSizes', []);

  // override flag as true DELVOD-25786
  const enableBigParcel = true;

  const itemWeightOptions = useMemo(
    () => {
      const options = [];
      for (let i = 1; i <= maxWeight; i++) {
        options.push({
          value: i,
          child: <span className={styles.itemCategoryOption}>{i} kg</span>,
        });
      }
      return options;
    },
    [maxWeight],
  );

  let parcelSizes = stateParcelSizes;
  if (_isEmpty(parcelSizes)) {
    parcelSizes = deliveryConfigParcelSizes;
  }

  const parcelSizeOptions = useMemo(
    () => {
      if (_isEmpty(parcelSizes)) {
        return [];
      }

      return parcelSizes.map(({ displaySize }) => ({
        value: displaySize,
        child: <span className={styles.itemCategoryOption}>{displaySize}</span>,
      }));
    },
    [parcelSizes],
  );

  const itemCategories = useSelector(itemCategoriesSelector);
  const itemTypeOptions = useMemo(
    () =>
      itemCategories.map(({ id, name }) => ({
        value: `${id}-${name}`,
        child: <span className={styles.itemCategoryOption}>{name}</span>,
      })),
    [itemCategories],
  );

  const itemWeightSuffix = useMemo(() => <span>KG</span>, []);

  const parcelSVGRender = useMemo(
    () => {
      if (!parcelTotalSize) {
        return null;
      }

      const configDisplaySize = deliveryConfigParcelSizes.find(
        size => size.displaySize === parcelTotalSize,
      );
      return !configDisplaySize ? null : (
        <img src={_get(configDisplaySize, 'iconURL')} alt="" />
      );
    },
    [parcelTotalSize, deliveryConfigParcelSizes],
  );

  const bigParcelItemWeightFieldOptions = useMemo(
    () => ({
      validateTrigger: 'onChange',
      rules: deliveryFormFields.itemWeight.makeRules({
        maxWeight,
        enableBigParcel,
      }),
      initialValue: _get(
        deliveryDetail,
        deliveryFormFields.itemWeight.fieldName,
      ),
    }),
    [deliveryDetail, maxWeight, enableBigParcel],
  );

  const isItemTypeMandatory = useSelector(isItemTypeMandatorySelector);
  const enableItemTypeFreeText = useSelector(enableItemTypeFreeTextSelector);

  const selectedItemType = form.getFieldValue(
    deliveryFormFields.itemType.fieldName,
  );
  let isOtherItemType = false;
  if (selectedItemType) {
    isOtherItemType = selectedItemType.startsWith('7-');
  }
  const showItemTypeFreeText = enableItemTypeFreeText && isOtherItemType;

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {I18n.t('v2.homepage.manualOrder.heading.itemDetails')}
      </div>
      {enableBigParcel && (
        <Row type="flex" gutter={16}>
          <Col xs={24} md={8} className={styles.parcelSvg}>
            {parcelSVGRender}
          </Col>
        </Row>
      )}
      <Row type="flex" gutter={16} className={styles.itemDetailSection}>
        {enableBigParcel && (
          <Col xs={24} md={8}>
            <RadioButton
              form={form}
              id={deliveryFormFields.parcelTotalSize.fieldName}
              fieldOptions={{
                initialValue: _get(
                  deliveryDetail,
                  deliveryFormFields.parcelTotalSize.fieldName,
                ),
              }}
              options={parcelSizeOptions}
              className={styles.parcelSizeWrapper}
              optionClassName={styles.parcelSize}
              disabled={disabled}
            />
          </Col>
        )}
        <Col xs={24} md={8}>
          {!enableBigParcel && (
            <SelectField
              form={form}
              id={deliveryFormFields.itemWeight.fieldName}
              fieldOptions={{
                initialValue: _get(
                  deliveryDetail,
                  deliveryFormFields.itemWeight.fieldName,
                ),
                rules: deliveryFormFields.itemWeight.makeRules({}),
              }}
              placeholder={I18n.t('v2.homepage.manualOrder.placeholder.weight')}
              options={itemWeightOptions}
              disabled={disabled}
            />
          )}
          {enableBigParcel && (
            <InputField
              form={form}
              type="number"
              suffix={itemWeightSuffix}
              id={deliveryFormFields.itemWeight.fieldName}
              fieldOptions={bigParcelItemWeightFieldOptions}
              placeholder={I18n.t('v2.homepage.manualOrder.placeholder.weight')}
              fieldClassName={styles.inputField}
              disabled={disabled}
            />
          )}
        </Col>
        <Col xs={24} md={8}>
          <SelectField
            form={form}
            id={deliveryFormFields.itemType.fieldName}
            fieldOptions={{
              initialValue: _get(
                deliveryDetail,
                deliveryFormFields.itemType.fieldName,
              ),
            }}
            placeholder={
              enableBigParcel && !isItemTypeMandatory
                ? I18n.t('v2.homepage.manualOrder.placeholder.itemTypeOptional')
                : I18n.t('v2.homepage.manualOrder.placeholder.itemType')
            }
            options={itemTypeOptions}
            disabled={disabled}
          />
        </Col>
        {showItemTypeFreeText && (
          <Col xs={24} md={8}>
            <InputField
              form={form}
              id={deliveryFormFields.itemTypeFreeText.fieldName}
              fieldOptions={{
                initialValue: _get(
                  deliveryDetail,
                  deliveryFormFields.itemTypeFreeText.fieldName,
                ),
              }}
              placeholder={I18n.t(
                'v2.homepage.manualOrder.placeholder.itemType',
              )}
              fieldClassName={styles.inputField}
              disabled={disabled}
            />
          </Col>
        )}
        {!enableBigParcel && (
          <Col xs={24} md={8}>
            <InputField
              form={form}
              id={deliveryFormFields.deliveryInstruction.fieldName}
              fieldOptions={{
                initialValue: _get(
                  deliveryDetail,
                  deliveryFormFields.deliveryInstruction.fieldName,
                ),
                rules: deliveryFormFields.deliveryInstruction.makeRules(),
              }}
              placeholder={I18n.t(
                'v2.homepage.manualOrder.placeholder.instruction',
              )}
              fieldClassName={styles.inputField}
              disabled={disabled}
            />
          </Col>
        )}
      </Row>
    </div>
  );
};

ItemDetails.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
  form: PropTypes.shape({}).isRequired,
  disabled: PropTypes.bool,
};

ItemDetails.defaultProps = {
  disabled: false,
};

export default ItemDetails;
